import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { useDevSettings } from '../../utils/useDevSettings';
import { AppPrincipal } from '../../auth/AppPrincipal';
import { ILightCareMap } from '../../caremap/ICareMap';
import { OrganizationsState } from '../OrganizationsState';
import { IClientInsight, IUnreadableClient, OrganizationInsightsState } from '../OrganizationInsightsState';
import {
    buildAgencyDigest,
    hasNothingReadable,
    historyProgress,
    IAgencyClient,
    IAgencyDigest,
    IAgencyFinding,
    IHistoryProgress,
    toAgencyFinding
} from './agencyDigest';
import { buildDemoInsights } from './agencyDemo';

const toFinding = (entry: IClientInsight): IAgencyFinding =>
    toAgencyFinding({ careMapId: entry.careMapId, clientName: entry.clientName, insight: entry.insight });

const toClient = (careMap: ILightCareMap): IAgencyClient => {
    // The organization's care map list is a projection, so read it defensively even though the type
    // says the patient node is always there.
    const patient = careMap.patient;
    const coverage = careMap.coverage;
    const name = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ');

    return {
        careMapId: careMap.careMapId,
        clientName: name || 'Unnamed client',
        // The server sends coverage only for a caremap this organization may read privately, so its
        // absence means unknown rather than empty.
        coverageKnown: Boolean(coverage),
        hasConfirmedCodeStatus: Boolean(coverage?.codeStatusConfirmed),
        hasInsuranceOnFile: Boolean(coverage?.insuranceOnFile),
        hasIdentityDocument: Boolean(coverage?.identityOnFile),
        lastRecordedAt: careMap.lastRecordedAt
    };
};

export interface IAgencyInsights {
    hasLoaded: boolean;
    /**
     * The organization has clients but could read none of them, so there is nothing to chart and
     * nothing honest to say in place of it. Neither lens draws in this state: every figure on them
     * is a ratio over the readable set, and over an empty one they read as findings about the book
     * rather than as the absence of one.
     */
    nothingReadable: boolean;
    /** The organization's caseload, or the invented one while the demo setting is on. */
    digest: IAgencyDigest;
    /** Whether the caseload has the history its comparisons need, and how far it has to go. */
    historyProgress: IHistoryProgress;
    isDemo: boolean;
    /**
     * Clients left out of every figure above, with the reason and enough to name them.
     *
     * A list rather than a count because the two reasons need different responses: a failed read
     * wants trying again, and a permission gap wants somebody to grant access. A reader given only
     * a number can do neither.
     */
    unreadableClients: IUnreadableClient[];
    /** Reads the caseload again, for the retry a failed read offers. */
    retry: () => Promise<void>;
}

/**
 * The caseload roll-up behind the Insights tab.
 *
 * A caseload too young to chart is still drawn from its own figures: the panels that compare across
 * weeks wait on `historyProgress` and say so, and the rest are real at any age. The invented sample
 * appears only under the admin-only demo setting, which skips reading the caseload at all.
 */
export const useAgencyInsights = (orgId: string): IAgencyInsights => {
    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const insightsState = useInstance(OrganizationInsightsState);
    useSubscription(() => insightsState);

    // Checked here as well as at the toggle: the setting stays on the device after the admin who set
    // it signs out, and whoever signs in next must see their own caseload.
    const principal = useInstance(AppPrincipal);
    const isDemo = useDevSettings().showInsightsDemo && principal.isAdmin === true;

    useAsyncEffect(async () => {
        if (isDemo) {
            return;
        }
        await insightsState.load(orgId);
    }, [orgId, insightsState, isDemo]);

    const retry = () => insightsState.reload(orgId);

    if (isDemo) {
        return buildDemoInsights({ now: new Date(), retry });
    }

    // A client whose findings could not be read is left out rather than passed in empty. Included,
    // they would draw as a full-score, steady cell on every diagram, which asserts something about a
    // person nobody has established; the tab names them separately instead, and every figure then
    // covers exactly the clients that were actually read. Note this is a different absence from the
    // evidence floor: that one is a client who IS readable and simply has too little history, and
    // it keeps them on the diagrams rather than dropping them.
    //
    // Both reasons are excluded, not just failed reads. A client this organization has no private
    // access to is the one most likely to arrive looking empty rather than looking broken.
    const unreadableClients = insightsState.getUnreadableClients();
    const unreadableIds = new Set(unreadableClients.map(client => client.careMapId));
    const clients = orgState.getOrganizationCareMaps(orgId)
        .filter(careMap => !unreadableIds.has(careMap.careMapId))
        .map(toClient);
    const findings = insightsState.getAttentionItems().map(toFinding);

    const digest = buildAgencyDigest({ findings, clients, now: new Date(), unreadableCount: unreadableClients.length });

    return {
        hasLoaded: insightsState.hasLoaded,
        nothingReadable: hasNothingReadable(digest),
        digest,
        historyProgress: historyProgress(digest),
        isDemo,
        unreadableClients,
        retry
    };
};
